import React, { useState, useEffect, useRef } from 'react';
import { Switch, Route, useHistory, useRouteMatch, useLocation } from 'react-router-dom';
import PrivateRoute from '../../components/PrivateRoute/PrivateRoute';
import DesignList from '../../components/DesignList/DesignList';
import TemplateCategorized from '../../components/Template/TemplateCategorized';
import TemplateList from '../../components/Template/TemplateList';
import Subscribe from '../../components/Subscribe/Subscribe';
import Support from '../../components/Contact/Contact';
import SmartAssistant from '../../components/SmartAssistant/SmartAssistant';
import Error from '../../components/Error/Error';
import PageName from './PageName';
import AccountSetting from '../../components/AccountSetting/AccountSetting';
import { useSelector, useDispatch } from 'react-redux';
import { userActions, designActions, subscriptionActions, templateActions, resourceActions } from '../../actions';
import {
  Button,
  Container,
  Icon,
  Menu,
  Responsive,
  Segment,
  Sidebar,
  Dropdown,
  Image,
  Dimmer,
  Header,
  Popup,
  List,
  Form,
  Grid,
  Divider,
  Message,
} from 'semantic-ui-react';
import Startup from './Startup';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import SearchBox from './SearchBox';
import _ from 'lodash';
import { centimeterToPixel, inchToPixel, millimeterToPixel } from '../../utils';
import { userService } from '../../services';

const getWidth = () => {
  const isSSR = typeof window === 'undefined';

  return isSSR ? Responsive.onlyTablet.minWidth : window.innerWidth;
};

const MainSection = props => {
  return (
    <div className="main">
      <Switch>
        <PrivateRoute path={props.path} exact component={Startup} />
        <PrivateRoute path={`${props.path}/design-list`} component={DesignList} />
        <PrivateRoute path={`${props.path}/templates`} exact component={TemplateCategorized} />
        <PrivateRoute path={`${props.path}/templates/:_type/:_query`} component={TemplateList} />
        <PrivateRoute path={`${props.path}/account-setting`} component={AccountSetting} />
        <PrivateRoute path={`${props.path}/subscribe`} component={Subscribe} />
        <PrivateRoute path={`${props.path}/assistant`} component={SmartAssistant} />
        <PrivateRoute path={`${props.path}/support`} component={Support} />
        <Route path="*" render={() => <Error error={'404'} redirectTo={'support'} />} />
      </Switch>
    </div>
  );
};

const DesktopContainer = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { path, url } = useRouteMatch();
  const dispatch = useDispatch();
  const user = useSelector(state => state.authentication.user);
  const designCreating = useSelector(state => state.design.creating);
  const subscription = useSelector(state => state.subscription.currentSubscription);
  const subRemainedDays = useSelector(state => state.subscription.subRemainedDays);
  const subStatus = useSelector(state => state.subscription.currentStatus);
  const changingAvatarState = useSelector(state => state.user.changingAvatarState);
  const templateCategories = useSelector(state => state.template.categories);
  const [activeDim, setActiveDim] = useState(false);
  const [reminderDim, setReminderDim] = useState(false);
  const [fixed, setFixed] = useState(false);
  const [activeItem, setActiveItem] = useState(location.pathname.replace(path, ''));
  const [customSizeForm, setCustomSizeForm] = useState(false);
  const [customSize, setCustomSize] = useState({});
  const [showCustomSizeError, setShowCustomSizeError] = useState(false);
  const customSizeUnitRef = useRef('px');
  const fileInputRef = useRef();
  const clientName = `${_.get(user, 'details.firstname', '')} ${_.get(user, 'details.lastname', '')}`.trim();
  const [LoadingDeleteAccount, setLoadingDeleteAccount] = useState(false)
  const customSizeUnitOptions = [
    {
      key: 1,
      text: t('pixel'),
      value: 'px',
    },
    {
      key: 2,
      text: t('inch'),
      value: 'in',
    },
    {
      key: 3,
      text: t('millimeter'),
      value: 'mm',
    },
    {
      key: 4,
      text: t('centimeter'),
      value: 'cm',
    },
  ];

  useEffect(() => {
    document.documentElement.dataset.theme = 'light';
    if (!subStatus && _.get(user, 'role') === 'client') {
      dispatch(subscriptionActions.getCurrentSubscription());
    }
    if (templateCategories.length === 0) dispatch(templateActions.getAllCategories());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (subRemainedDays > 0 && subRemainedDays < 4) {
      setReminderDim(true);
    }
  }, [subRemainedDays]);

  useEffect(() => {
    if (changingAvatarState === 'success') {
      let timestamp = new Date().getTime();
      let el = document.getElementById('avatar-1');
      if (el)
        el.style.backgroundImage = `url(${process.env.REACT_APP_FILE_SERVER}/users/${user.userId}/avatar.png?t=${timestamp})`;
      el = document.getElementById('avatar-2');
      if (el)
        el.style.backgroundImage = `url(${process.env.REACT_APP_FILE_SERVER}/users/${user.userId}/avatar.png?t=${timestamp})`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changingAvatarState]);

  const logout = () => {
    dispatch(subscriptionActions.resetState());
    dispatch(userActions.logout());
    history.push('/');
  };

  const notify = () => toast.error(t('changeAvatarErrorSize'));

  const changeProfilePicture = event => {
    if (event.target.files[0].size > 110000) {
      notify();
    } else {
      dispatch(userActions.changeProfilePicture([...event.target.files]));
    }
  };

  const closeDimmers = () => {
    setActiveDim(false);
    setReminderDim(false);
  };

  const showCustomSizeForm = () => {
    setCustomSize({});
    setShowCustomSizeError(false);
    setCustomSizeForm(true);
  };

  const createNewDesign = category => {
    dispatch(designActions.create({
      size: { width: 1600, height: 1600 },
      templateCategory: category.name,
      educationContext: category.code === 'free'
        ? { mode: 'free', topicCodes: [] }
        : { mode: 'lesson', gradeCode: category.code, topicCodes: [] },
    }));
  };

  const handleCustomSizeChanged = (e, { name, value }) => {
    setCustomSize({ ...customSize, [name]: value });
    if (value) {
      let converted = parseInt(value);
      switch (customSizeUnitRef.current) {
        case 'px':
          converted = value;
          break;
        case 'cm':
          converted = Math.round(centimeterToPixel(value));
          break;
        case 'mm':
          converted = Math.round(millimeterToPixel(value));
          break;
        case 'in':
          converted = Math.round(inchToPixel(value));
          break;
      }
      setShowCustomSizeError(converted < 40 || converted > 8000);
    }
  };

  const handleCustomSizeUnitChanged = (e, { value }) => {
    customSizeUnitRef.current = value;
    let size;
    switch (customSizeUnitRef.current) {
      case 'px':
        size = { ...customSize };
        break;
      case 'cm':
        size = {
          width: Math.round(centimeterToPixel(customSize.width)),
          height: Math.round(centimeterToPixel(customSize.height)),
        };
        break;
      case 'mm':
        size = {
          width: Math.round(millimeterToPixel(customSize.width)),
          height: Math.round(millimeterToPixel(customSize.height)),
        };
        break;
      case 'in':
        size = {
          width: Math.round(inchToPixel(customSize.width)),
          height: Math.round(inchToPixel(customSize.height)),
        };
        break;
    }
    if (
      (!!size.width && (size.width < 40 || size.width > 8000)) ||
      (!!size.height && (size.height < 40 || size.height > 8000))
    ) {
      setShowCustomSizeError(true);
    } else {
      setShowCustomSizeError(false);
    }
  };

  const createCustomSizeDesign = () => {
    let size;
    switch (customSizeUnitRef.current) {
      case 'px':
        size = { ...customSize };
        break;
      case 'cm':
        size = {
          width: Math.round(centimeterToPixel(customSize.width)),
          height: Math.round(centimeterToPixel(customSize.height)),
        };
        break;
      case 'mm':
        size = {
          width: Math.round(millimeterToPixel(customSize.width)),
          height: Math.round(millimeterToPixel(customSize.height)),
        };
        break;
      case 'in':
        size = {
          width: Math.round(inchToPixel(customSize.width)),
          height: Math.round(inchToPixel(customSize.height)),
        };
        break;
    }
    if (size.width < 40 || size.width > 8000 || size.height < 40 || size.height > 8000) {
      setShowCustomSizeError(true);
    } else dispatch(designActions.create({ size }));
  };

  const profileBtn = (
    <div className="profileBtn">
      <div
        id="avatar-1"
        className="profileAvatar"
        style={{
          backgroundImage: `url(${process.env.REACT_APP_FILE_SERVER}/users/${user.userId}/avatar.png)`,
        }}
      ></div>
      <div className="profileText">
        <span>{_.isEmpty(clientName) ? t('noName') : clientName}</span>
        <span className="email">{user.username}</span>
      </div>
      <div className="profileArrow">
        <Icon name="angle down" size="small" />
      </div>
    </div>
  );

  const handleItemClick = (e, { path }) => {
    if (activeDim) setActiveDim(false);
    setActiveItem(path);
    history.push(`${url}${path}`);
  };

  const handleDeleteAccount = () => {


    const sendData = {
      username: user.username
    }

    setLoadingDeleteAccount(true)
    userService.deleteAccount(sendData)
    .then(() => {      
      setLoadingDeleteAccount(false)
      window.location.href = "/"
    }).catch(err => {
      console.log(err)
      setLoadingDeleteAccount(false)
    })
  }

  return (
    <Responsive className="mainContainer" getWidth={getWidth} minWidth={Responsive.onlyTablet.minWidth}>
      <div className="clientHomeHeader">
        <div className="container">
          <Dropdown trigger={profileBtn} className="profileDD" pointing="top left" icon={null}>
            <Dropdown.Menu>
              <Dropdown.Header>
                <div className="profileHeader">
                  <div
                    id="avatar-2"
                    className="profileHeaderAvatar"
                    role="button"
                    tabIndex={0}
                    title={t('addAvatar')}
                    onClick={() => fileInputRef.current.click()}
                    onKeyDown={event => {
                      if (event.key === 'Enter' || event.key === ' ') fileInputRef.current.click();
                    }}
                    style={{
                      backgroundImage: `url(${process.env.REACT_APP_FILE_SERVER}/users/${user.userId}/avatar.png)`,
                    }}
                  ></div>
                  <div className="profileHeaderText">
                    <span>{_.isEmpty(clientName) ? t('noName') : clientName}</span>
                    <span className="email">{user.username}</span>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png"
                      hidden
                      onChange={changeProfilePicture}
                    />
                  </div>
                </div>
              </Dropdown.Header>
              <div className="subscriptionPeriod">
                <span>{t('subscriptionRemained')}:</span>
                <span>
                  {subRemainedDays} {t('day')}
                </span>
              </div>
              <Dropdown.Menu scrolling className="profileMenu">
                <Dropdown.Item
                  key="1"
                  text={t('accountSettings')}
                  onClick={() => history.push(`${url}/account-setting`)}
                />
                <Dropdown.Item key="2" text={t('help')} />
                <Dropdown.Item key="3" text={t('logout')} onClick={logout} />
                <Dropdown.Item key="4" content={
                  <>
                    <Button
                      size="large"
                      color='red'
                      // style={{ marginTop: 45, marginBottom: 15 }}
                      loading={LoadingDeleteAccount}
                      onClick={handleDeleteAccount}
                      content={t('deleteAccount')}
                    />
                  </>
                } />
              </Dropdown.Menu>
            </Dropdown.Menu>
          </Dropdown>
          <PageName path={activeItem} />
          <SearchBox />
          <Popup
            className="categorySizePopup"
            on="click"
            basic
            trigger={
              <Button
                loading={designCreating}
                style={{ marginLeft: '0.5em' }}
                className="vButton colorPrimary createDesign"
                content={t('createDesign')}
              />
            }
          >
            <Popup.Content>
              {customSizeForm ? (
                <Form className="customSizeForm" size="small">
                  <Grid>
                    <Grid.Column textAlign="left">
                      <Icon name="delete" onClick={() => setCustomSizeForm(false)} />
                    </Grid.Column>
                  </Grid>
                  <Divider />
                  <Form.Group widths="equal">
                    <Form.Field>
                      <Form.Input
                        name="width"
                        label={t('width')}
                        error={_.get(customSize, 'width', undefined) === ''}
                        onChange={handleCustomSizeChanged}
                      />
                    </Form.Field>
                    <Form.Field>
                      <Form.Input
                        name="height"
                        label={t('height')}
                        error={_.get(customSize, 'height', undefined) === ''}
                        onChange={handleCustomSizeChanged}
                      />
                    </Form.Field>
                    <Form.Field>
                      <Form.Select
                        defaultValue="px"
                        options={customSizeUnitOptions}
                        onChange={handleCustomSizeUnitChanged}
                      />
                    </Form.Field>
                  </Form.Group>
                  <Message error content={t('customSizeErrorMessage')} visible={showCustomSizeError} />
                  <Button
                    basic
                    fluid
                    className="vButton"
                    content={t('createDesign')}
                    disabled={_.isEmpty(customSize.width) || _.isEmpty(customSize.height)}
                    onClick={createCustomSizeDesign}
                    loading={designCreating}
                  />
                </Form>
              ) : (
                <List selection verticalAlign="middle">
                  {templateCategories.map((category, index) => (
                    <List.Item key={index} className="categoryItem" onClick={() => createNewDesign(category)}>
                      <List.Content>
                        <i className={`icon ${_.camelCase(category.name)}`}></i>
                        <span>{t(category.name)}</span>
                        <span className="categoryItemSize">
                          <span
                            style={{ direction: 'ltr', display: 'inline-block' }}
                          >{`${category.size.width} x ${category.size.height}`}</span>
                          {` ${t('pixel')}`}
                        </span>
                      </List.Content>
                    </List.Item>
                  ))}
                </List>
              )}
            </Popup.Content>
          </Popup>
        </div>
      </div>
      <div className="homeContainer">
        <div className="fixSide">
          <div>
            <Menu
              fixed={fixed ? 'top' : null}
              // inverted={!fixed}
              pointing={!fixed}
              secondary={!fixed}
              size="large"
            >
              <div className="menuContainer">
                <Menu.Item active={activeItem === ''} as="a" path="" onClick={handleItemClick}>
                  <span className="tooltiptext">{t('home')}</span>
                  <img src={`${process.env.PUBLIC_URL}/assets/images/menuIcon/home.svg`} />
                </Menu.Item>
                <Menu.Item
                  active={activeItem === '/design-list'}
                  as="a"
                  path="/design-list"
                  onClick={handleItemClick}
                >
                  <span className="tooltiptext">{t('yourDesigns')}</span>
                  <img src={`${process.env.PUBLIC_URL}/assets/images/menuIcon/myTemp.svg`} />
                </Menu.Item>
                <Menu.Item
                  active={activeItem === '/templates'}
                  as="a"
                  path="/templates"
                  onClick={handleItemClick}
                >
                  <span className="tooltiptext">{t('templates')}</span>
                  <img src={`${process.env.PUBLIC_URL}/assets/images/menuIcon/temp.svg`} />
                </Menu.Item>
                {/* <Menu.Item
                  active={activeItem === '/subscribe'}
                  as="a"
                  path="/subscribe"
                  content={t('subscribe')}
                  onClick={handleItemClick}
                >
                  <span className="tooltiptext">{t('subscribe')}</span>
                  <img src={`${process.env.PUBLIC_URL}/assets/images/menuIcon/subscribe.svg`} />
                </Menu.Item> */}
                <Menu.Item as="a">
                  {/* <span>{t('photos')}</span>
                  <span className="comingSoon">({t('comingSoon')})</span> */}
                  <span className="tooltiptext">
                    {t('photos')} <span className="comingSoon">({t('comingSoon')})</span>
                  </span>
                  <img src={`${process.env.PUBLIC_URL}/assets/images/menuIcon/photo.svg`} />
                </Menu.Item>
                <Menu.Item
                  active={activeItem === '/assistant'}
                  as="a"
                  path="/assistant"
                  onClick={handleItemClick}
                >
                  <span className="tooltiptext">{t('smartAssistant')}</span>
                  <img src={`${process.env.PUBLIC_URL}/assets/images/menuIcon/support.svg`} />
                </Menu.Item>
              </div>
            </Menu>
          </div>
        </div>
        <Dimmer.Dimmable as="div" className="mainSection" blurring={true} onClick={() => closeDimmers()}>
          <MainSection path={path} />

          <Dimmer active={activeDim} blurring="true" className="noSubscribe">
            <Header as="h2" icon>
              {t('noSubscription')}
              <br />
              <br />
              <Button
                className="vButton colorPrimary"
                content={t('buyNewSubscription')}
                onClick={() => history.push(`${url}/subscribe`)}
              />
            </Header>
          </Dimmer>
          <Dimmer active={reminderDim} blurring="true">
            <Header as="h2" icon inverted>
              {`${t('subscriptionReminderP1')} ${subRemainedDays} ${t('subscriptionReminderP2')}`}
              <br />
              <br />
              <Button content={t('buyNewSubscription')} onClick={() => history.push(`${url}/subscribe`)} />
              <Button content={t('later')} onClick={() => setReminderDim(false)} />
            </Header>
          </Dimmer>
        </Dimmer.Dimmable>
      </div>
    </Responsive>
  );
};

const MobileContainer = () => {
  const { t } = useTranslation();
  const { path, url } = useRouteMatch();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector(state => state.authentication.user);
  const subscription = useSelector(state => state.subscription.currentSubscription);
  const subRemainedDays = useSelector(state => state.subscription.subRemainedDays);
  const subStatus = useSelector(state => state.subscription.currentStatus);
  const templateCategories = useSelector(state => state.template.categories);
  const [activeDim, setActiveDim] = useState(false);
  const [reminderDim, setReminderDim] = useState(false);
  const designCreating = useSelector(state => state.design.creating);
  const [sidebarOpened, setSidebarOpened] = useState(false);
  const [activeItem, setActiveItem] = useState(location.pathname.replace(path, ''));
  const [customSizeForm, setCustomSizeForm] = useState(false);
  const [customSize, setCustomSize] = useState({});
  const [showCustomSizeError, setShowCustomSizeError] = useState(false);
  const customSizeUnitRef = useRef('px');
  const fileInputRef = useRef();
  const [LoadingDeleteAccount, setLoadingDeleteAccount] = useState(false)

  const clientName = `${_.get(user, 'details.firstname', '')} ${_.get(user, 'details.lastname', '')}`.trim();
  const customSizeUnitOptions = [
    {
      key: 1,
      text: t('pixel'),
      value: 'px',
    },
    {
      key: 2,
      text: t('inch'),
      value: 'in',
    },
    {
      key: 3,
      text: t('millimeter'),
      value: 'mm',
    },
    {
      key: 4,
      text: t('centimeter'),
      value: 'cm',
    },
  ];

  useEffect(() => {
    if (!subStatus && _.get(user, 'role') === 'client') {
      dispatch(subscriptionActions.getCurrentSubscription());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (subRemainedDays > 0 && subRemainedDays < 4) {
      setReminderDim(true);
    }
  }, [subRemainedDays]);

  const logout = () => {
    dispatch(userActions.logout());
    history.push('/');
    setSidebarOpened(false);
  };

  const handleItemClick = (e, { path }) => {
    if (activeDim) setActiveDim(false);
    setActiveItem(path);
    history.push(`${url}${path}`);
    setSidebarOpened(false);
  };

  const closeDimmers = () => {
    setActiveDim(false);
    setReminderDim(false);
  };

  const createNewDesign = category => {
    dispatch(designActions.create({
      size: { width: 1600, height: 1600 },
      templateCategory: category.name,
      educationContext: category.code === 'free'
        ? { mode: 'free', topicCodes: [] }
        : { mode: 'lesson', gradeCode: category.code, topicCodes: [] },
    }));
  };

  const settingClick = () => {
    history.push(`${url}/account-setting`);
    setSidebarOpened(false);
  };

  const showCustomSizeForm = () => {
    setCustomSize({});
    setShowCustomSizeError(false);
    setCustomSizeForm(true);
  };

  const handleCustomSizeChanged = (e, { name, value }) => {
    setCustomSize({ ...customSize, [name]: value });
    if (value) {
      let converted = parseInt(value);
      switch (customSizeUnitRef.current) {
        case 'px':
          converted = value;
          break;
        case 'cm':
          converted = Math.round(centimeterToPixel(value));
          break;
        case 'mm':
          converted = Math.round(millimeterToPixel(value));
          break;
        case 'in':
          converted = Math.round(inchToPixel(value));
          break;
      }
      setShowCustomSizeError(converted < 40 || converted > 8000);
    }
  };

  const handleCustomSizeUnitChanged = (e, { value }) => {
    customSizeUnitRef.current = value;
    let size;
    switch (customSizeUnitRef.current) {
      case 'px':
        size = { ...customSize };
        break;
      case 'cm':
        size = {
          width: Math.round(centimeterToPixel(customSize.width)),
          height: Math.round(centimeterToPixel(customSize.height)),
        };
        break;
      case 'mm':
        size = {
          width: Math.round(millimeterToPixel(customSize.width)),
          height: Math.round(millimeterToPixel(customSize.height)),
        };
        break;
      case 'in':
        size = {
          width: Math.round(inchToPixel(customSize.width)),
          height: Math.round(inchToPixel(customSize.height)),
        };
        break;
    }
    if (
      (!!size.width && (size.width < 40 || size.width > 8000)) ||
      (!!size.height && (size.height < 40 || size.height > 8000))
    ) {
      setShowCustomSizeError(true);
    } else {
      setShowCustomSizeError(false);
    }
  };

  const createCustomSizeDesign = () => {
    let size;
    switch (customSizeUnitRef.current) {
      case 'px':
        size = { ...customSize };
        break;
      case 'cm':
        size = {
          width: Math.round(centimeterToPixel(customSize.width)),
          height: Math.round(centimeterToPixel(customSize.height)),
        };
        break;
      case 'mm':
        size = {
          width: Math.round(millimeterToPixel(customSize.width)),
          height: Math.round(millimeterToPixel(customSize.height)),
        };
        break;
      case 'in':
        size = {
          width: Math.round(inchToPixel(customSize.width)),
          height: Math.round(inchToPixel(customSize.height)),
        };
        break;
    }
    if (size.width < 40 || size.width > 8000 || size.height < 40 || size.height > 8000) {
      setShowCustomSizeError(true);
    } else dispatch(designActions.create({ size }));
  };

  const handleDeleteAccount = () => {


    const sendData = {
      username: user.username
    }

    setLoadingDeleteAccount(true)
    userService.deleteAccount(sendData).then(({ data }) => {
      window.location.href = "/"
      setLoadingDeleteAccount(false)
    }).catch(err => {
      console.log(err)
      setLoadingDeleteAccount(false)
    })
  }

  return (
    <Responsive
      className="mobileContainer"
      as={Sidebar.Pushable}
      getWidth={getWidth}
      maxWidth={Responsive.onlyMobile.maxWidth}
    >
      <Sidebar
        as={Menu}
        direction="right"
        animation="push"
        inverted
        onHide={() => setSidebarOpened(false)}
        vertical
        visible={sidebarOpened}
      >
        <div className="profileHeader" onClick={settingClick}>
          <div className="profileHeaderAvatar">
            {(_.has(user, 'details.firstname') && user.details.firstname.charAt(0)) || '?'}
          </div>
          <div className="profileHeaderText">
            <span>{_.isEmpty(clientName) ? t('noName') : clientName}</span>
            <span className="email">{user.username}</span>
          </div>
        </div>
        <div className="subscriptionPeriod">
          <span>{t('subscriptionRemained')}:</span>
          <span>
            {subRemainedDays} {t('day')}
          </span>
        </div>
        <div className="menuContainer">
          <div className="sideBtnBB">
            <span onClick={settingClick}>
              <Icon inverted name="setting" size="massive" />
            </span>
            <span>
              <Icon inverted name="help" size="massive" />
            </span>
            <span onClick={logout}>
              <Icon inverted name="sign-out" size="massive" />
            </span>
          </div>
          <Button
            size="large"
            color='red'
            style={{display : "block" ,margin :"auto", marginBottom: 15 }}
            loading={LoadingDeleteAccount}
            onClick={handleDeleteAccount}
            content={t('deleteAccount')}
            
          />
          <Menu.Item active={activeItem === ''} as="a" path="" content={t('home')} onClick={handleItemClick} />
          <Popup
            className="categorySizePopup"
            on="click"
            basic
            trigger={
              <Menu.Item
                // active={activeItem === ''}
                as="a"
                path=""
                content={t('createDesign')}
              />
            }
          >
            <Popup.Content>
              {customSizeForm ? (
                <Form className="customSizeForm" size="small">
                  <Grid>
                    <Grid.Column textAlign="left">
                      <Icon name="delete" onClick={() => setCustomSizeForm(false)} />
                    </Grid.Column>
                  </Grid>
                  <Divider />
                  <Form.Group widths="equal">
                    <Form.Field>
                      <Form.Input
                        name="width"
                        label={t('width')}
                        error={_.get(customSize, 'width', undefined) === ''}
                        onChange={handleCustomSizeChanged}
                      />
                    </Form.Field>
                    <Form.Field>
                      <Form.Input
                        name="height"
                        label={t('height')}
                        error={_.get(customSize, 'height', undefined) === ''}
                        onChange={handleCustomSizeChanged}
                      />
                    </Form.Field>
                    <Form.Field>
                      <Form.Select
                        defaultValue="px"
                        options={customSizeUnitOptions}
                        onChange={handleCustomSizeUnitChanged}
                      />
                    </Form.Field>
                  </Form.Group>
                  <Message error content={t('customSizeErrorMessage')} visible={showCustomSizeError} />
                  <Button
                    basic
                    fluid
                    className="vButton"
                    content={t('createDesign')}
                    disabled={_.isEmpty(customSize.width) || _.isEmpty(customSize.height)}
                    onClick={createCustomSizeDesign}
                    loading={designCreating}
                  />
                </Form>
              ) : (
                <List selection verticalAlign="middle">
                  {templateCategories.map((category, index) => (
                    <List.Item key={index} className="categoryItem" onClick={() => createNewDesign(category)}>
                      <List.Content>
                        {t(category.name)}
                        <span className="categoryItemSize">
                          {`${category.size.width} x ${category.size.height} ${t('pixel')}`}
                        </span>
                      </List.Content>
                    </List.Item>
                  ))}
                </List>
              )}
            </Popup.Content>
          </Popup>
          <Menu.Item
            active={activeItem === '/design-list'}
            as="a"
            path="/design-list"
            content={t('yourDesigns')}
            onClick={handleItemClick}
          />
          <Menu.Item
            active={activeItem === '/templates'}
            as="a"
            path="/templates"
            content={t('templates')}
            onClick={handleItemClick}
          />
          <Menu.Item
            active={activeItem === '/subscribe'}
            as="a"
            path="/subscribe"
            content={t('subscribe')}
            onClick={handleItemClick}
          />
          <Menu.Item as="a">
            <span>{t('photos')}</span>
            <span className="comingSoon">({t('comingSoon')})</span>
          </Menu.Item>
          <Menu.Item
            active={activeItem === '/assistant'}
            as="a"
            path="/assistant"
            content={t('smartAssistant')}
            onClick={handleItemClick}
          />
        </div>
      </Sidebar>

      <Sidebar.Pusher dimmed={sidebarOpened}>
        <Segment textAlign="center" style={{ minHeight: 350, padding: '1em 0em' }} vertical>
          <Container>
            <Menu pointing secondary size="large">
              <Menu.Item position="right">
                <div className="vibinoLogo"></div>
                {/* <Image className="vibinoLogo" src={require('../../assets/images/vibinoLogoYellow.svg')} /> */}
              </Menu.Item>
              <Menu.Item position="left" onClick={() => setSidebarOpened(true)}>
                <Icon name="sidebar" />
              </Menu.Item>
            </Menu>
          </Container>
          <Dimmer.Dimmable as="div" className="mainSection" blurring onClick={() => closeDimmers()}>
            <MainSection path={path} />
            <div>
              <Popup
                className="categorySizePopup"
                on="click"
                basic
                trigger={
                  <Button
                    loading={designCreating}
                    style={{ marginLeft: '0.5em' }}
                    className="mobileNewDesign vButton colorPrimary"
                    content={t('createDesign')}
                  />
                }
              >
                <Popup.Content>
                  {customSizeForm ? (
                    <Form className="customSizeForm" size="small">
                      <Grid>
                        <Grid.Column textAlign="left">
                          <Icon name="delete" onClick={() => setCustomSizeForm(false)} />
                        </Grid.Column>
                      </Grid>
                      <Divider />
                      <Form.Group widths="equal">
                        <Form.Field>
                          <Form.Input
                            name="width"
                            label={t('width')}
                            error={_.get(customSize, 'width', undefined) === ''}
                            onChange={handleCustomSizeChanged}
                          />
                        </Form.Field>
                        <Form.Field>
                          <Form.Input
                            name="height"
                            label={t('height')}
                            error={_.get(customSize, 'height', undefined) === ''}
                            onChange={handleCustomSizeChanged}
                          />
                        </Form.Field>
                        <Form.Field>
                          <Form.Select
                            defaultValue="px"
                            options={customSizeUnitOptions}
                            onChange={(e, { value }) => (customSizeUnitRef.current = value)}
                          />
                        </Form.Field>
                      </Form.Group>
                      <Message error content={t('customSizeErrorMessage')} visible={showCustomSizeError} />
                      <Button
                        basic
                        fluid
                        className="vButton"
                        content={t('createDesign')}
                        disabled={_.isEmpty(customSize.width) || _.isEmpty(customSize.height)}
                        onClick={createCustomSizeDesign}
                        loading={designCreating}
                      />
                    </Form>
                  ) : (
                    <List selection verticalAlign="middle">
                      {templateCategories.map((category, index) => (
                        <List.Item key={index} className="categoryItem" onClick={() => createNewDesign(category)}>
                          <List.Content>
                            {t(category.name)}
                            <span className="categoryItemSize">
                              {`${category.size.width} x ${category.size.height} ${t('pixel')}`}
                            </span>
                          </List.Content>
                        </List.Item>
                      ))}
                    </List>
                  )}
                </Popup.Content>
              </Popup>
            </div>
            <Dimmer active={activeDim} blurring="true">
              <Header as="h2" icon inverted>
                {t('noSubscription')}
                <br />
                <br />
                <Button content={t('buyNewSubscription')} onClick={() => history.push(`${url}/subscribe`)} />
              </Header>
            </Dimmer>
            <Dimmer active={reminderDim} blurring="true">
              <Header as="h2" icon inverted>
                {`${t('subscriptionReminderP1')} ${subRemainedDays} ${t('subscriptionReminderP2')}`}
                <br />
                <br />
                <Button content={t('buyNewSubscription')} onClick={() => history.push(`${url}/subscribe`)} />
                <Button content={t('later')} onClick={() => setReminderDim(false)} />
              </Header>
            </Dimmer>
          </Dimmer.Dimmable>
        </Segment>
      </Sidebar.Pusher>
    </Responsive>
  );
};

const ResponsiveContainer = () => (
  <>
    <DesktopContainer />
    <MobileContainer />
  </>
);

const ClientHome = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const designId = useSelector(state => state.design.designId);
  const initRef = useRef(true);

  useEffect(() => {
    if (initRef.current && designId) {
      initRef.current = false;
      dispatch(resourceActions.resetState());
      dispatch(templateActions.resetState());
      dispatch(designActions.resetState());
      return;
    }
    if (initRef.current) initRef.current = false;

    designId && history.push(`/edit/design/${designId}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designId]);

  return <ResponsiveContainer></ResponsiveContainer>;
};

MainSection.propTypes = {
  path: PropTypes.string,
};

export default ClientHome;
